'use client';

import React, { useState, useMemo } from 'react';
import { Plus, Download, Guitar } from 'lucide-react';
import { toast } from 'sonner';
import { mockGuitars, Guitar as GuitarType, GuitarStatus, GuitarType as GuitarTypeEnum } from './mockData';
import CollectionFilters from './CollectionFilters';
import GuitarTable from './GuitarTable';
import AddGuitarModal from './AddGuitarModal';
import CollectionStats from './CollectionStats';

export default function GuitarCollectionContent() {
  const [guitars, setGuitars] = useState<GuitarType[]>(mockGuitars);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<GuitarStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<GuitarTypeEnum | 'all'>('all');
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [sortKey, setSortKey] = useState<keyof GuitarType>('purchaseDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);

  const uniqueBrands = useMemo(() => Array.from(new Set(guitars.map((g) => g.brand))).sort(), [guitars]);

  const filtered = useMemo(() => {
    let result = [...guitars];
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (g) =>
          g.brand.toLowerCase().includes(q) ||
          g.model.toLowerCase().includes(q) ||
          g.serialNumber.toLowerCase().includes(q) ||
          g.color.toLowerCase().includes(q) ||
          g.purchaseLocation.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') result = result.filter((g) => g.status === statusFilter);
    if (typeFilter !== 'all') result = result.filter((g) => g.type === typeFilter);
    if (brandFilter !== 'all') result = result.filter((g) => g.brand === brandFilter);

    result.sort((a, b) => {
      const av = a[sortKey] as string;
      const bv = b[sortKey] as string;
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [guitars, search, statusFilter, typeFilter, brandFilter, sortKey, sortDir]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  function handleSort(key: keyof GuitarType) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  }

  function handleDeleteSelected() {
    setGuitars((prev) => prev.filter((g) => !selectedIds.has(g.id)));
    toast.success(`${selectedIds.size} guitar${selectedIds.size > 1 ? 's' : ''} removed from vault`);
    setSelectedIds(new Set());
  }

  function handleDeleteOne(id: string) {
    setGuitars((prev) => prev.filter((g) => g.id !== id));
    toast.success('Guitar removed from vault');
    setSelectedIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
  }

  function handleStatusChange(id: string, status: GuitarStatus) {
    setGuitars((prev) => prev.map((g) => (g.id === id ? { ...g, status } : g)));
    toast.success('Status updated');
  }

  function handleAddGuitar(data: Omit<GuitarType, 'id'>) {
    const newGuitar: GuitarType = {
      ...data,
      id: `guitar-${String(guitars.length + 1).padStart(3, '0')}`,
    };
    setGuitars((prev) => [newGuitar, ...prev]);
    setAddModalOpen(false);
    toast.success(`${data.brand} ${data.model} added to your vault`);
  }

  function handleExport() {
    toast.info('Exporting collection as CSV…');
  }

  return (
    <div className="min-h-full bg-background" suppressHydrationWarning>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-12 py-6 xl:py-8 space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-700 text-foreground tracking-tight">My Guitar Collection</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {guitars.filter((g) => g.status === 'active').length} active · {guitars.length} total instruments
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-500 text-muted-foreground border border-border rounded-lg hover:bg-muted transition-all duration-150 active:scale-95"
            >
              <Download size={14} />
              Export CSV
            </button>
            <button
              onClick={() => setAddModalOpen(true)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-600 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-all duration-150 active:scale-95 shadow-sm"
            >
              <Plus size={14} />
              Add Guitar
            </button>
          </div>
        </div>

        <CollectionStats guitars={guitars} />

        <CollectionFilters
          search={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          statusFilter={statusFilter}
          onStatusChange={(v) => { setStatusFilter(v); setPage(1); }}
          typeFilter={typeFilter}
          onTypeChange={(v) => { setTypeFilter(v); setPage(1); }}
          brandFilter={brandFilter}
          onBrandChange={(v) => { setBrandFilter(v); setPage(1); }}
          uniqueBrands={uniqueBrands}
        />

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-xl px-4 py-2.5 slide-up">
            <p className="text-sm font-600 text-primary">
              {selectedIds.size} guitar{selectedIds.size > 1 ? 's' : ''} selected
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-xs font-500 text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded hover:bg-muted"
              >
                Deselect all
              </button>
              <button
                onClick={handleDeleteSelected}
                className="text-xs font-600 text-negative hover:bg-negative/10 px-3 py-1.5 rounded-lg transition-colors border border-negative/20"
              >
                Remove {selectedIds.size} from vault
              </button>
            </div>
          </div>
        )}

        <GuitarTable
          guitars={paginated}
          selectedIds={selectedIds}
          onSelectChange={setSelectedIds}
          onDelete={handleDeleteOne}
          onStatusChange={handleStatusChange}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={handleSort}
          allIds={filtered.map((g) => g.id)}
          page={page}
          pageSize={pageSize}
          totalCount={filtered.length}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          emptySearch={search.trim() !== '' || statusFilter !== 'all' || typeFilter !== 'all' || brandFilter !== 'all'}
        />

        <AddGuitarModal
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onAdd={handleAddGuitar}
        />
      </div>
    </div>
  );
}